import { Link } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, UserCircle, Heart, Sun, Moon, Gamepad2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";


const categories = [
  { label: "All Gear", to: "/shop" },
  { label: "Brands", to: "/brands" },
  { label: "Peripherals", to: "/shop?cat=Peripherals" },
  { label: "Audio", to: "/shop?cat=Audio" },
  { label: "Setup Gear", to: "/shop?cat=Setup+Gear" },
  { label: "Gaming Merch", to: "/shop?cat=Gaming+Merch" },
  { label: "Collectibles", to: "/shop?cat=Collectibles" },
  { label: "Accessories", to: "/shop?cat=Accessories" },
  { label: "Blog", to: "/blog" },
  { label: "🔥 Today's Deals", to: "/shop?sort=price-low" },
];

const Navbar = () => {
  const { totalItems } = useCart();
  const { user } = useAuth();
  const { wishlistIds } = useWishlist();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <>
      
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-[hsl(var(--arcade-violet)/0.25)] bg-background/70 backdrop-blur-2xl">
        <div className="pointer-events-none absolute inset-0 grid-bg-fine opacity-30" />

        {/* Top Row */}
        <div className="container relative mx-auto flex h-16 items-center gap-4 px-4">
          <Link to="/" className="group flex items-center gap-2 font-display text-xl font-black tracking-tight shrink-0">
            <span className="relative flex h-8 w-8 items-center justify-center rounded-md border border-[hsl(var(--arcade-cyan)/0.4)] bg-gradient-to-br from-[hsl(var(--arcade-violet)/0.3)] to-[hsl(var(--arcade-cyan)/0.15)] shadow-[0_0_20px_hsl(var(--arcade-violet)/0.4)] transition-transform group-hover:rotate-12">
              <Gamepad2 className="h-4 w-4 text-[hsl(var(--arcade-cyan))]" />
            </span>
            <span className="text-arcade-gradient">GROVEFAN</span>
          </Link>

          {/* Search Bar */}
          <div className="relative hidden flex-1 max-w-2xl md:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--arcade-cyan))]" />
            <input
              type="text"
              placeholder="// search mice, keyboards, headsets, merch..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && search.trim()) {
                  window.location.href = `/shop?q=${encodeURIComponent(search)}`;
                }
              }}
              className="w-full rounded-full border border-[hsl(var(--arcade-violet)/0.3)] bg-[hsl(var(--card)/0.6)] py-2.5 pl-10 pr-4 font-mono text-xs text-foreground placeholder:text-muted-foreground/60 focus:border-[hsl(var(--arcade-cyan)/0.6)] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--arcade-cyan)/0.2)]"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">⌘K</span>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              aria-label="Toggle theme"
              onClick={toggleTheme}
              className="rounded-full p-2 transition-all hover:bg-[hsl(var(--arcade-violet)/0.15)] hover:text-[hsl(var(--arcade-cyan))]"
            >
              {theme === "dark" ? <Sun className="h-5 w-5 text-muted-foreground" /> : <Moon className="h-5 w-5 text-muted-foreground" />}
            </button>
            <Link to="/shop" aria-label="Search" className="rounded-full p-2 transition-colors hover:bg-[hsl(var(--arcade-violet)/0.15)] md:hidden">
              <Search className="h-5 w-5 text-muted-foreground" />
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" className="group relative rounded-full p-2 transition-all hover:bg-[hsl(var(--arcade-violet)/0.15)]">
              <Heart className={`h-5 w-5 transition-colors ${wishlistIds.length > 0 ? "fill-current text-[hsl(var(--destructive))]" : "text-muted-foreground group-hover:text-[hsl(var(--arcade-cyan))]"}`} />
              {wishlistIds.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--destructive))] px-1 font-mono text-[9px] font-bold text-white shadow-[0_0_10px_hsl(var(--destructive)/0.6)]">
                  {wishlistIds.length}
                </span>
              )}
            </Link>
            <Link to="/cart" aria-label="Cart" className="group relative rounded-full p-2 transition-all hover:bg-[hsl(var(--arcade-violet)/0.15)]">
              <ShoppingBag className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-[hsl(var(--arcade-cyan))]" />
              {totalItems > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--arcade-cyan))] px-1 font-mono text-[9px] font-bold text-[hsl(var(--background))] shadow-[0_0_10px_hsl(var(--arcade-cyan)/0.6)]">
                  {totalItems}
                </span>
              )}
            </Link>
            <Link
              to={user ? "/account" : "/login"}
              aria-label={user ? "Account" : "Sign in"}
              className="rounded-full p-2 transition-colors hover:bg-[hsl(var(--arcade-violet)/0.15)]"
            >
              <UserCircle className={`h-5 w-5 ${user ? "text-[hsl(var(--arcade-cyan))]" : "text-muted-foreground"}`} />
            </Link>
            <button aria-label="Toggle menu" className="rounded-full p-2 transition-colors hover:bg-[hsl(var(--arcade-violet)/0.15)] md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Category Strip — Desktop */}
        <div className="hidden border-t border-[hsl(var(--arcade-violet)/0.15)] bg-[hsl(var(--surface)/0.4)] md:block overflow-hidden">
          <div className="container mx-auto flex items-center gap-0 px-4 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.label}
                to={cat.to}
                className="relative whitespace-nowrap px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground transition-all hover:text-[hsl(var(--arcade-cyan))] after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:w-0 after:-translate-x-1/2 after:bg-[hsl(var(--arcade-cyan))] after:transition-all hover:after:w-full"
              >
                {cat.label}
              </Link>
            ))}
            <Link
              to="/sell"
              className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--arcade-violet))] brightness-125 transition-colors hover:text-[hsl(var(--arcade-cyan))]"
            >
              ▲ Start Selling
            </Link>
            <Link
              to="/affiliate"
              className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--arcade-cyan))] transition-colors hover:brightness-125"
            >
              ◆ Affiliate
            </Link>
          </div>
        </div>

        {/* Neon underglow */}
        <div className="h-[2px]" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--arcade-violet)), hsl(var(--arcade-cyan)), hsl(var(--arcade-magenta)), transparent)" }} />



        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-border/50 bg-background md:hidden"
            >
              <div className="flex flex-col gap-1 p-4">
                {/* Mobile search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-md border border-border bg-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                </div>
                <p className="px-2 text-[10px] font-bold uppercase tracking-widest text-primary">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.label}
                    to={cat.to}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                  >
                    {cat.label}
                  </Link>
                ))}
                <div className="my-2 h-px bg-border" />
                <Link to="/" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">Home</Link>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">About</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">Contact</Link>
                <Link to="/sell" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-bold text-primary hover:bg-secondary">🚀 Start Selling</Link>
                <Link to="/affiliate" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-bold text-primary hover:bg-secondary">💰 Affiliate</Link>
                <Link
                  to={user ? "/account" : "/login"}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary"
                >
                  {user ? "My Account" : "Sign In"}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
